import { useMemo } from 'react';
import * as R from 'remeda';
import { useQuery } from '@tanstack/react-query';
import { endpoints, ID } from '@rossum/api-client';
import { CustomRelation, Relation } from '@rossum/api-client/relations';
import { api } from '../../../lib/apiClient';
import { useCustomRelationsConfig } from './useCustomRelationsConfig';

export const CUSTOM_RELATIONS_QUERY_KEY = 'custom-relations';

export const isCustomRelation = (
  relation: Relation
): relation is CustomRelation => relation.type === 'custom';

// Sub-types missing from ui_settings.custom_relations are hidden by design.
export const useFetchAnnotationCustomRelations = (annotationId: ID | null) => {
  const { isEnabled, allowedSubtypes } = useCustomRelationsConfig();

  const { data, isInitialLoading, isError } = useQuery({
    queryKey: [CUSTOM_RELATIONS_QUERY_KEY, 'annotation', annotationId],
    queryFn: async () => {
      if (!annotationId)
        throw new Error('Annotation id is required to fetch custom relations');

      const relations = await Promise.all([
        api.unpaginatedRequest(endpoints.relations.list)({
          type: 'custom',
          parent: [annotationId],
        }),
        api.unpaginatedRequest(endpoints.relations.list)({
          type: 'custom',
          annotation: [annotationId],
        }),
      ]);

      return R.pipe(
        relations,
        R.flat(),
        R.uniqueBy(r => r.id)
      );
    },
    enabled: isEnabled && !!annotationId,
  });

  const customRelations = useMemo(() => {
    const allowedNames = allowedSubtypes.map(subtype => subtype.name);

    return (data ?? [])
      .filter(isCustomRelation)
      .filter(relation => allowedNames.includes(relation.name));
  }, [allowedSubtypes, data]);

  return { customRelations, isInitialLoading, isError };
};
